import { store } from '../../../../widget/state/store';
import type { IterationUsageRow } from '../../../../widget/types/iteration-usage';

export interface ConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface ConversationDetail {
  id: string;
  messages: Array<{ role: string; content: string | unknown[]; tool_call_id?: string; turnId?: string | null }>;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  status: string;
  errorMessage: string | null;
}

function getBaseUrl(): string {
  return store.getState().config.serverUrl;
}

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = store.getState().auth.token;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function parseJsonResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    store.setAuth({ status: 'unauthenticated', email: null, token: null });
    throw new Error('Session expired. Please sign in again.');
  }

  if (!res.ok) {
    throw new Error(`Request failed: ${res.status}`);
  }

  return res.json() as Promise<T>;
}

export interface ConversationListResponse {
  conversations: ConversationSummary[];
  hasMore: boolean;
}

export const portalApi = {
  async listConversations(cursor?: string, limit = 15): Promise<ConversationListResponse> {
    const widgetId = store.getState().config.widgetId;
    const params = new URLSearchParams();
    if (widgetId) params.set('widgetId', widgetId);
    params.set('limit', String(limit));
    if (cursor) params.set('cursor', cursor);

    const url = `${getBaseUrl()}/webchat/my-conversations?${params.toString()}`;
    const res = await fetch(url, {
      headers: getHeaders(),
      credentials: 'include',
    });
    const data = await parseJsonResponse<any>(res);
    const conversations: ConversationSummary[] = data.conversations ?? data;
    return {
      conversations,
      hasMore: data.hasMore ?? conversations.length >= limit,
    };
  },

  async getConversation(id: string): Promise<ConversationDetail> {
    const url = `${getBaseUrl()}/webchat/my-conversations/${encodeURIComponent(id)}`;
    const res = await fetch(url, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return parseJsonResponse<ConversationDetail>(res);
  },

  async getIterationUsage(conversationId: string): Promise<IterationUsageRow[]> {
    const url = `${getBaseUrl()}/webchat/conversations/${encodeURIComponent(conversationId)}/iteration-usage`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
      throw new Error(`Request failed: ${res.status}`);
    }
    return res.json() as Promise<IterationUsageRow[]>;
  },

  async deleteConversation(id: string): Promise<void> {
    const url = `${getBaseUrl()}/webchat/my-conversations/${encodeURIComponent(id)}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    if (res.status === 401) {
      store.setAuth({ status: 'unauthenticated', email: null, token: null });
      throw new Error('Session expired. Please sign in again.');
    }
    if (!res.ok) throw new Error(`Failed to delete conversation: ${res.status}`);
  },
};
